import React, { useCallback, useRef, useState } from "react";
import { UploadCloud, X, GripVertical, Loader2, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const resolveUrl = (u) => (u && u.startsWith("/") ? `${BACKEND}${u}` : u);

async function uploadOne(file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await api.post("/admin/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data.url; // relative path e.g. /api/static/uploads/abc.jpg
}

async function rotateOne(url, degrees = 90) {
  const res = await api.post("/admin/rotate", { url, degrees });
  const rotatedUrl = res.data?.url || url;
  return `${rotatedUrl}${rotatedUrl.includes("?") ? "&" : "?"}t=${Date.now()}`;
}

/**
 * Single-image upload with drag+drop and preview.
 * `value` is the stored URL string (may be relative like /api/static/uploads/... or absolute).
 */
export const ImageDropzone = ({ value, onChange, testId = "image-dropzone" }) => {
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = useCallback(
    async (files) => {
      const file = files?.[0];
      if (!file) return;
      if (!file.type.startsWith("image/")) {
        toast.error("Please choose an image file");
        return;
      }
      setUploading(true);
      try {
        const url = await uploadOne(file);
        onChange(url);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
      } finally {
        setUploading(false);
      }
    },
    [onChange],
  );

  return (
    <div>
      {value ? (
        <div className="relative w-full aspect-[16/10] bg-sand-dark border border-ink/10 overflow-hidden">
          <img src={resolveUrl(value)} alt="Cover" className="w-full h-full object-cover" />
          <button
            type="button"
            onClick={async () => {
              try {
                const nu = await rotateOne(value, 90);
                onChange(nu);
              } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail) || "Rotate failed");
              }
            }}
            className="absolute top-3 right-14 bg-sand text-ink hover:bg-forest hover:text-sand rounded-full w-8 h-8 flex items-center justify-center transition-colors"
            aria-label="Rotate 90°"
            data-testid={`${testId}-rotate`}
          >
            <RotateCw size={14} />
          </button>
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute top-3 right-3 bg-sand text-ink hover:bg-red-600 hover:text-sand rounded-full w-8 h-8 flex items-center justify-center transition-colors"
            aria-label="Remove"
            data-testid={`${testId}-remove`}
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            handleFiles(e.dataTransfer.files);
          }}
          onClick={() => inputRef.current?.click()}
          className={`relative w-full aspect-[16/10] border-2 border-dashed cursor-pointer flex flex-col items-center justify-center gap-2 transition-colors ${
            dragging ? "border-forest bg-sand-dark/50" : "border-ink/20 bg-sand hover:border-forest"
          }`}
          data-testid={testId}
        >
          {uploading ? (
            <>
              <Loader2 size={26} className="text-forest animate-spin" />
              <p className="text-sm text-stone">Uploading…</p>
            </>
          ) : (
            <>
              <UploadCloud size={26} className="text-forest" />
              <p className="text-sm text-ink">Drag & drop or click to upload</p>
              <p className="text-xs text-stone">JPG, PNG, WebP · up to 10 MB</p>
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid={`${testId}-input`}
          />
        </div>
      )}
    </div>
  );
};

/**
 * Multi-image gallery uploader with drag+drop, previews, remove, and reorder (drag handle).
 * `value` is an array of URL strings.
 */
export const GalleryDropzone = ({ value = [], onChange, testId = "gallery-dropzone" }) => {
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);
  const dragIdx = useRef(null);

  const handleFiles = useCallback(
    async (files) => {
      const arr = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
      if (arr.length === 0) return;
      setUploading(true);
      try {
        const urls = [];
        for (const f of arr) {
          try {
            urls.push(await uploadOne(f));
          } catch (e) {
            toast.error(`${f.name}: ${formatApiError(e.response?.data?.detail) || "upload failed"}`);
          }
        }
        if (urls.length) onChange([...(value || []), ...urls]);
      } finally {
        setUploading(false);
      }
    },
    [onChange, value],
  );

  const removeAt = (i) => onChange(value.filter((_, idx) => idx !== i));

  const onDragStart = (i) => (dragIdx.current = i);
  const onDropReorder = (target) => {
    const src = dragIdx.current;
    if (src == null || src === target) return;
    const arr = [...value];
    const [moved] = arr.splice(src, 1);
    arr.splice(target, 0, moved);
    onChange(arr);
    dragIdx.current = null;
  };

  return (
    <div className="space-y-3">
      {value.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {value.map((u, i) => (
            <div
              key={`${u}-${i}`}
              draggable
              onDragStart={() => onDragStart(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDropReorder(i)}
              className="relative aspect-[4/3] bg-sand-dark border border-ink/10 overflow-hidden group cursor-move"
              data-testid={`${testId}-item-${i}`}
            >
              <img src={resolveUrl(u)} alt={`Gallery ${i + 1}`} className="w-full h-full object-cover" />
              <span className="absolute top-2 left-2 bg-ink/60 text-sand rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <GripVertical size={12} />
              </span>
              <button
                type="button"
                onClick={async () => {
                  try {
                    const nu = await rotateOne(u, 90);
                    const arr = [...value];
                    arr[i] = nu;
                    onChange(arr);
                  } catch (e) {
                    toast.error(formatApiError(e.response?.data?.detail) || "Rotate failed");
                  }
                }}
                className="absolute top-2 right-10 bg-sand text-ink hover:bg-forest hover:text-sand rounded-full w-7 h-7 flex items-center justify-center transition-colors"
                aria-label="Rotate 90°"
                data-testid={`${testId}-item-${i}-rotate`}
              >
                <RotateCw size={11} />
              </button>
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="absolute top-2 right-2 bg-sand text-ink hover:bg-red-600 hover:text-sand rounded-full w-7 h-7 flex items-center justify-center transition-colors"
                aria-label="Remove"
                data-testid={`${testId}-item-${i}-remove`}
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          handleFiles(e.dataTransfer.files);
        }}
        onClick={() => inputRef.current?.click()}
        className={`w-full aspect-[16/5] border-2 border-dashed cursor-pointer flex flex-col items-center justify-center gap-1.5 transition-colors ${
          dragging ? "border-forest bg-sand-dark/50" : "border-ink/20 bg-sand hover:border-forest"
        }`}
        data-testid={testId}
      >
        {uploading ? (
          <>
            <Loader2 size={22} className="text-forest animate-spin" />
            <p className="text-xs text-stone">Uploading…</p>
          </>
        ) : (
          <>
            <UploadCloud size={22} className="text-forest" />
            <p className="text-sm text-ink">Drop multiple photos here, or click to select</p>
            <p className="text-xs text-stone">Drag thumbnails to reorder</p>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid={`${testId}-input`}
        />
      </div>
    </div>
  );
};
